import { UserRole } from '../services/authService';
import { jwtUtils } from '../utils/jwtUtils';
import { storageService } from '../services/storageService';

// Define the route permissions configuration
export const routePermissions: Record<string, UserRole[]> = {
    '/': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/indigent-dashboard': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/add': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/edit/:requestId': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/user-action': ['gensize_admin', 'gensize_data_approver'],
    '/approval': ['gensize_data_approver', 'gensize_data_worker', 'gensize_admin'],
    '/users': ['gensize_admin', 'gensize_data_approver'],
    '/add-user': ['gensize_admin'],
    '/edit-user/:userId': ['gensize_admin'],
    '/reports/indigent-register': ['gensize_admin', 'gensize_data_approver'],
    '/reports/dashboard': ['gensize_admin', 'gensize_data_approver'],
    '/it-user-action': ['gensize_admin', 'gensize_data_approver'],
    '/my-account': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/dashboard': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/business-online-enquiry': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/account-verification-service': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/cipc-report': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/consumer-trace': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/deeds-enquiry': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/digital-onboarding-verification-system': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/director-enquiry-report': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/faceview-plus': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/kyc-online': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/realtime-id-verification': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/realtime-marriage-verification': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/realtime-idv': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/telephone-trace': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
    '/verification/history': ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'],
};

// Centralized role checking functions
export const rolePermissions = {
    // Navigation permissions
    canAccessAdd: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_data_worker', 'gensize_data_approver', 'gensize_admin'].includes(role));
    },

    canAccessApproval: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_data_approver', 'gensize_data_worker', 'gensize_admin'].includes(role));
    },

    canAccessUsers: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_admin', 'gensize_data_approver'].includes(role));
    },

    canAccessReports: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_admin', 'gensize_data_approver'].includes(role));
    },

    canAccessActions: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_admin', 'gensize_data_approver'].includes(role));
    },

    // Action permissions
    canPerformActions: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_admin', 'gensize_data_approver'].includes(role));
    },

    canManageUsers: (userRoles: UserRole[]): boolean => {
        return userRoles.some(role => ['gensize_admin'].includes(role));
    },

    // Check if user has any of the required roles
    hasAnyRole: (userRoles: UserRole[], requiredRoles: UserRole[]): boolean => {
        return userRoles.some(role => requiredRoles.includes(role));
    },

    // Check if user has all required roles
    hasAllRoles: (userRoles: UserRole[], requiredRoles: UserRole[]): boolean => {
        return requiredRoles.every(role => userRoles.includes(role));
    },

    // Navigation filtering functions - moved from Sidebar logic
    getFilteredNavItems: (userRoles: UserRole[], navItems: Array<{ label: string, icon: React.ReactNode, path: string }>): Array<{ label: string, icon: React.ReactNode, path: string }> => {
        if (!userRoles || userRoles.length === 0) return [];

        // Check if user has admin role (highest privileges)
        if (userRoles.includes('gensize_admin')) {
            return navItems; // Show all items
        }

        // For users with multiple roles, combine permissions from all roles
        const allowedPaths = new Set<string>();

        // Check if user has data worker role
        if (userRoles.includes('gensize_data_worker')) {
            allowedPaths.add('/');
            allowedPaths.add('/add');
            allowedPaths.add('/approval');
        }

        // Check if user has data approver role
        if (userRoles.includes('gensize_data_approver')) {
            // Add home path for data approver
            allowedPaths.add('/');
            // Add all paths except /add for data approver
            navItems.forEach(item => {
                if (item.path !== '/add') {
                    allowedPaths.add(item.path);
                }
            });
        }

        // Return filtered items based on combined permissions
        return navItems.filter(item => allowedPaths.has(item.path));
    },

    getFilteredReportItems: (userRoles: UserRole[], reportItems: Array<{ label: string, path: string }>): Array<{ label: string, path: string }> => {
        if (!userRoles || userRoles.length === 0) return [];

        // Check if user has admin role (highest privileges)
        if (userRoles.includes('gensize_admin')) {
            return reportItems; // Show all reports
        }

        // For users with multiple roles, combine permissions from all roles
        const allowedReports = new Set<string>();

        // Check if user has data approver role
        if (userRoles.includes('gensize_data_approver')) {
            reportItems.forEach(item => allowedReports.add(item.path));
        }

        // Check if user has data worker role
        if (userRoles.includes('gensize_data_worker')) {
            // Data workers cannot access reports (no reports added to allowedReports)
        }

        // Return filtered items based on combined permissions
        return reportItems.filter(item => allowedReports.has(item.path));
    },

    getFilteredActionItems: (userRoles: UserRole[], actionItems: Array<{ label: string, path: string }>): Array<{ label: string, path: string }> => {
        if (!userRoles || userRoles.length === 0) return [];

        // Check if user has admin role (highest privileges)
        if (userRoles.includes('gensize_admin')) {
            return actionItems; // Show all actions
        }

        // For users with multiple roles, combine permissions from all roles
        const allowedActions = new Set<string>();

        // Check if user has data approver role
        if (userRoles.includes('gensize_data_approver')) {
            actionItems.forEach(item => allowedActions.add(item.path));
        }

        // Check if user has data worker role
        if (userRoles.includes('gensize_data_worker')) {
            // Data workers cannot access actions (no actions added to allowedActions)
        }

        // Return filtered items based on combined permissions
        return actionItems.filter(item => allowedActions.has(item.path));
    }
};

// Get user roles directly from JWT token for security
export const getUserRolesFromToken = (): UserRole[] => {
    const token = storageService.getToken();
    if (!token?.access_token) return [];

    const decodedUser = jwtUtils.decodeToken(token.access_token);
    if (!decodedUser?.roles) return [];

    // Filter and validate roles from token
    const validRoleTypes: UserRole[] = ['gensize_admin', 'gensize_data_worker', 'gensize_data_approver'];
    const validRoles: UserRole[] = [];

    decodedUser.roles.forEach((role: string) => {
        if (validRoleTypes.includes(role as UserRole)) {
            validRoles.push(role as UserRole);
        }
    });

    return validRoles;
};

// Helper function to check if a route is accessible by roles
export const hasRouteAccess = (path: string, userRoles?: UserRole[]): boolean => {
    if (!userRoles || userRoles.length === 0) return false;

    // Check exact path match first
    if (routePermissions[path]) {
        // Check if user has ANY of the required roles for this route
        return routePermissions[path].some(requiredRole => userRoles.includes(requiredRole));
    }

    // Check pattern matches (for dynamic routes like /edit/:requestId)
    for (const routePattern of Object.keys(routePermissions)) {
        if (routePattern.includes(':')) {
            // Convert pattern to regex
            const regexPattern = routePattern.replace(/:[^/]+/g, '[^/]+');
            const regex = new RegExp(`^${regexPattern}$`);
            if (regex.test(path)) {
                // Check if user has ANY of the required roles for this route
                return routePermissions[routePattern].some(requiredRole => userRoles.includes(requiredRole));
            }
        }
    }

    return false;
};
